// Basic
import React from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useDeviceSizeStore from '@/app/store/deviceSize';
// Components
import RenderDateWithFromTime from '../date/RenderDateWithFromTime';
import RenderInternationalizedAmount from '../currency/RenderInternationalizedAmount';
import Entity from '../entity/Entity';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from '../../ui/tooltip';
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Common
import ICONS from '@/common-utils/enums/icons';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import getObjectAttributeFromStringKey from '@/common-utils/functions/getObjectAttributeFromStringKey';
import { flexRender } from '@tanstack/react-table';
import { FIELD_TYPES } from './FieldTypes';
import getTranslatedText from '@/common-utils/functions/getTranslatedText';
// Style
import './DetailsGrid.scss';
// Lang
import { nsOrganization } from '@/app/i18n/namespaces';

/**
 *
 * @param root0
 * @param root0.data
 * @param root0.fields
 * @param root0.editableFields
 * @param root0.permissions
 * @param root0.showEmptyFields
 * @param root0.singleColumnLayout
 * @param root0.isModal
 * @param root0.columnsNumber
 * @returns {JSX} DetailsGrid
 */
function DetailsGrid({ data, fields, editableFields = [], permissions = [], showEmptyFields = true, isModal = false, singleColumnLayout = false, columnsNumber = 2 }) {

    // Store
    const { isSmallDevice, isLargeDevice } = useDeviceSizeStore();

    // Helper function to format data based on type
    const formatValue = (value, field) => {
        if (!value && value !== false) { return <span></span>; }
        switch (field.type) {
            case FIELD_TYPES.COMPONENT:
                {
                    const FieldComponent = field.component;
                    return <FieldComponent value={value} field={field} />;
                }
            case FIELD_TYPES.AMOUNT:
                return <RenderInternationalizedAmount
                    amount={value?.amount}
                    currency={value?.currency}
                    className='text-sm text-black'
                />;
            case FIELD_TYPES.DATE:
                return <RenderDateWithFromTime value={value} className='text-left font-bold' aligned />;
            case FIELD_TYPES.DATE_NO_TIME:
                return <RenderDateWithFromTime value={value} className='text-left font-bold' noTime aligned />;
            case FIELD_TYPES.DATE_TIME_SHORT:
                return <RenderDateWithFromTime value={value} className='text-left font-bold' noRelative noSeconds aligned />;
            case FIELD_TYPES.BADGE:
                {
                    const badgeConfig = field.enum?.[value];
                    if (!badgeConfig) {
                        return <span></span>;
                    }
                    return (
                        <Badge
                            entity={badgeConfig.entity}
                            namespace={badgeConfig.namespace}
                            variant={badgeConfig.variant}
                        />
                    );
                }
            case FIELD_TYPES.MULTI_ENTITY:
                {
                    if (!Array.isArray(value) || value.length === 0) {
                        return <span></span>;
                    }
                    return (
                        <TooltipProvider delayDuration={0}>
                            <div className='flex flex-col gap-1'>
                                {value.map((item) => {
                                    const suffix = typeof item === 'string' ? item : item.key;
                                    const hasEnabledStatus = typeof item === 'object' && item !== null && 'enabled' in item;
                                    return (
                                        <div key={suffix} className='flex items-center gap-1.5 font-bold'>
                                            <Entity
                                                entity={`${field.entityPrefix ?? ''}${suffix}`}
                                                namespace={field.namespace}
                                            />
                                            {hasEnabledStatus && (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <span
                                                            className={cn(
                                                                'inline-block h-2 w-2 shrink-0 rounded-full',
                                                                item.enabled ? 'bg-success' : 'bg-danger'
                                                            )}
                                                        />
                                                    </TooltipTrigger>
                                                    <TooltipContent side='top' className='text-sm font-normal'>
                                                        <Entity
                                                            entity={`${field.entityPrefix ?? ''}${item.enabled ? 'enabled' : 'disabled'}`}
                                                            namespace={field.namespace}
                                                        />
                                                    </TooltipContent>
                                                </Tooltip>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </TooltipProvider>
                    );
                }
            case FIELD_TYPES.TEXT:
                {
                    return (
                        <Entity
                            className='font-bold'
                            entity={`${field.prefix ?? ''}${value}`}
                            namespace={field.namespace}
                        />
                    );

                }
            case FIELD_TYPES.PORT:
                {
                    return (
                        <div className='font-bold'>
                            {
                                getTranslatedText(value >= 90000 ?
                                    'details.connection_points.port.redirected' : 'details.connection_points.port.tlsv1_2', nsOrganization, { port: value })
                            }
                        </div>
                    );

                }
            case FIELD_TYPES.LINK:
                {
                    return (
                        <a href={value} target='_blank' className='text-blue-600 font-bold dark:text-blue-500 hover:underline'>
                            {value}
                        </a>
                    );

                }
            case FIELD_TYPES.COLOR:
                return (
                    <div className='small-color-preview rounded-sm' style={{ backgroundColor: value }}>
                        {value}
                    </div>
                );
            case FIELD_TYPES.IMAGE:
                {
                    return (
                        <img alt='' src={value} className='reduced-img-size rounded-sm'
                            onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = field.placeholder;
                            }}
                        />
                    );
                }
            case FIELD_TYPES.BOOLEAN:
                return (
                    <div>
                        <Entity entity={value ? 'button.yes' : 'button.no'} className='font-bold' />
                        {flexRender(field.extraInfo, { show: value })}
                    </div>
                );
            default:
                return <div className='break-all'>{value}</div>;
        }
    };

    const getEditableField = (field) => {
        return editableFields.find(editableField =>
            editableField.fieldKey === generateNestedFieldAccessor(field)
        );
    };

    const hasPermissions = (field) => {
        const editableField = getEditableField(field);
        return hasRequiredPermissions({
            permissions: permissions,
            requiredPermissions: editableField?.requiredPermissions
        });
    };

    const isEditable = (field) => {
        return editableFields.some(editableField =>
            editableField.fieldKey === generateNestedFieldAccessor(field)
        );
    };

    // Renders
    const renderField = (field) => {
        const fieldKey = field.parentKeys ? generateNestedFieldAccessor(field) : field.key;
        const fieldData = data[fieldKey] ?? getObjectAttributeFromStringKey(data, fieldKey);
        const showField = showEmptyFields || !!fieldData;

        return (
            <React.Fragment key={fieldKey}>
                {showField &&
                    <>
                        <div className='text-labelGray-foreground'>
                            <Entity entity={field.entity} namespace={field.namespace} />
                        </div>
                        <div className='font-bold min-w-0'>
                            {isEditable(field) && (
                                <Button
                                    className='edit-button'
                                    variant='outline'
                                    icon={ICONS.EDIT}
                                    onClick={
                                        hasPermissions(field)
                                            ? getEditableField(field)?.onClick
                                            : null
                                    }
                                    disabled={!hasPermissions(field)}
                                />
                            )}
                            {data && formatValue(fieldData, field)}
                        </div>
                    </>
                }
            </React.Fragment >
        );
    };

    // Constants — balanced columns when singleColumnLayout (e.g. Payludd): matches former mock grid-cols-2 for nicer firmware wraps
    const containerClassName = isSmallDevice
        ? 'details-grid-container'
        : singleColumnLayout
            ? 'details-grid-container details-grid-container-min details-grid-container-balanced'
            : 'details-grid-container details-grid-container-min';

    if (singleColumnLayout) {
        return (
            <div className='details-grid-wrapper min-w-0 w-full max-w-full'>
                <div className={containerClassName}>
                    {Object.values(fields).map((field) => renderField(field))}
                </div>
            </div>
        );
    }

    if (isModal) {
        return (
            <div className='modal-grid-wrapper'>
                {/* Display Column 1 */}
                <div className={containerClassName}>
                    {Object.values(fields)
                        .slice(0, Math.ceil(Object.values(fields).length / 2))
                        .map((field) => renderField(field))}
                </div>
                {/* Display Column 2 */}
                <div className={containerClassName}>
                    {Object.values(fields)
                        .slice(Math.ceil(Object.values(fields).length / 2))
                        .map((field) => renderField(field))}
                </div>
            </div>
        );
    }
    return (
        <div
            className={isLargeDevice ? 'details-grid-wrapper details-grid-wrapper-max' : 'details-grid-wrapper'}
            style={columnsNumber > 2 ? {
                '--details-grid-label-max': '100px',
                ...(isLargeDevice && { '--details-grid-columns': `repeat(${columnsNumber}, 1fr)` })
            } : undefined}
        >
            {(() => {
                const fieldsArray = Object.values(fields);
                const columnSize = Math.ceil(fieldsArray.length / columnsNumber);
                return Array.from({ length: columnsNumber }, (_, columnIndex) => (
                    <div key={columnIndex} className={containerClassName}>
                        {fieldsArray
                            .slice(columnIndex * columnSize, (columnIndex + 1) * columnSize)
                            .map((field) => renderField(field))}
                    </div>
                ));
            })()}
        </div>
    );
}

DetailsGrid.propTypes = {
    data: PropTypes.object,
    fields: PropTypes.object,
    editableFields: PropTypes.array,
    permissions: PropTypes.array,
    showEmptyFields: PropTypes.bool,
    isModal: PropTypes.bool,
    singleColumnLayout: PropTypes.bool,
    columnsNumber: PropTypes.number
};
export default DetailsGrid;
